import type { CustomerMasterContract, CustomerMasterCustomer, CustomerMasterReport } from '@vy/types';
import type { CustomerMasterRow } from '../customer-master-report-page-components/customer-master-report-page-table/customer-master-row-types';

/**
 * Reshape a nested `CustomerMasterReport` into a flat list of one row per
 * (customer, contract) pair. Customers with no contracts produce a single row
 * with empty contract fields. Customers are sorted alphabetically by name and,
 * within a customer, contracts are sorted by `startDate` descending — matching
 * the convention the export already uses.
 *
 * The customer's `status` is renamed to `customerStatus` on the row so it does
 * not collide with the contract's `status` during the spread.
 */
export function buildFlatCustomerMasterRows(report: CustomerMasterReport): CustomerMasterRow[] {
  const sortedCustomers = [...report].sort((leftCustomer, rightCustomer) =>
    leftCustomer.customerName.localeCompare(rightCustomer.customerName, undefined, { sensitivity: 'base' }),
  );

  return sortedCustomers.flatMap((customer) => buildRowsForCustomer(customer));
}

function buildRowsForCustomer(customer: CustomerMasterCustomer): CustomerMasterRow[] {
  const { contracts, status: customerStatus, ...customerFieldsExcludingStatus } = customer;
  const customerRowId = customer.customerId ?? customer.customerName;

  if (contracts.length === 0) {
    return [{
      ...customerFieldsExcludingStatus,
      customerStatus,
      id: `${customerRowId}::no-contract`,
    }];
  }

  const sortedContracts = [...contracts].sort(
    (leftContract, rightContract) => rightContract.startDate.getTime() - leftContract.startDate.getTime(),
  );

  return sortedContracts.map((contract) =>
    mergeCustomerAndContract(customerFieldsExcludingStatus, customerStatus, contract, customerRowId),
  );
}

function mergeCustomerAndContract(
  customerFieldsExcludingStatus: Omit<CustomerMasterCustomer, 'contracts' | 'status'>,
  customerStatus: CustomerMasterCustomer['status'],
  contract: CustomerMasterContract,
  customerRowId: string,
) {
  const { status: contractStatus, ...contractFieldsExcludingStatus } = contract;

  return {
    ...customerFieldsExcludingStatus,
    customerStatus,
    ...contractFieldsExcludingStatus,
    contractStatus,
    // The contract spread would otherwise shadow the customer's customFieldValues.
    customFieldValues: customerFieldsExcludingStatus.customFieldValues,
    contractCustomFieldValues: contract.customFieldValues,
    id: `${customerRowId}::${contract.contractId}`,
  } satisfies CustomerMasterRow;
}
